"use client";

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type DragEvent,
} from "react";

/** The DataTransfer "type" browsers report while files are being dragged. */
enum DragTransferType {
  Files = "Files",
}

enum DropEffect {
  Copy = "copy",
}

enum FileDropItemKind {
  File = "file",
}

export interface FileDropZoneHandlers {
  onDragEnter: (event: DragEvent) => void;
  onDragOver: (event: DragEvent) => void;
  onDragLeave: (event: DragEvent) => void;
  onDrop: (event: DragEvent) => void;
}

export interface UseFileDropZoneInput {
  /** Invoked with the dropped files once a file drop completes. */
  onFilesDropped: (files: File[]) => void;
  /** When true, drag tracking is inert and the overlay never activates. */
  disabled?: boolean;
}

export interface UseFileDropZoneResult {
  /** True while a file drag hovers the zone — drive the overlay with this. */
  isDraggingFiles: boolean;
  dropZoneHandlers: FileDropZoneHandlers;
}

function dataTransferHasFiles(dataTransfer: DataTransfer | null): boolean {
  if (!dataTransfer) {
    return false;
  }
  // `types` is a DOMStringList in some engines; normalize before checking.
  if (Array.from(dataTransfer.types ?? []).includes(DragTransferType.Files)) {
    return true;
  }
  // Some sources expose the file only via `items` (kind === "file") and not in
  // the `types` list — check both so the zone reliably engages for any file.
  return Array.from(dataTransfer.items ?? []).some(
    (item) => item.kind === FileDropItemKind.File,
  );
}

function dragCarriesFiles(event: DragEvent): boolean {
  return dataTransferHasFiles(event.dataTransfer);
}

function readDroppedFiles(dataTransfer: DataTransfer | null): File[] {
  if (!dataTransfer) return [];
  const items = Array.from(dataTransfer.items ?? []);
  const canInspectEntries =
    items.length > 0 && typeof items[0].webkitGetAsEntry === "function";
  // Fallback for engines without the items entry API.
  if (!canInspectEntries) return Array.from(dataTransfer.files ?? []);
  return items
    .filter((item) => item.kind === FileDropItemKind.File)
    .filter((item) => {
      // webkitGetAsEntry is synchronous and only reliable during drop.
      const entry = item.webkitGetAsEntry();
      return !entry || !entry.isDirectory;
    })
    .map((item) => item.getAsFile())
    .filter((file): file is File => file != null);
}

/**
 * Tracks a file drag over a target element and surfaces a boolean for a
 * "drop to attach" overlay. An enter/leave depth counter keeps the overlay
 * steady while the cursor crosses child elements. Non-file drags (text, links,
 * in-editor selections) are ignored so the overlay only appears for real files.
 *
 * The returned handlers are meant to be spread onto the drop target; the host
 * owns the visual overlay (positioned `pointerEvents: none`) so it never
 * intercepts these events.
 */
export function useFileDropZone({
  onFilesDropped,
  disabled = false,
}: UseFileDropZoneInput): UseFileDropZoneResult {
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  const dragDepthRef = useRef(0);

  const resetDrag = useCallback(() => {
    dragDepthRef.current = 0;
    setIsDraggingFiles(false);
  }, []);

  const onDragEnter = useCallback(
    (event: DragEvent) => {
      if (disabled || !dragCarriesFiles(event)) {
        return;
      }
      event.preventDefault();
      dragDepthRef.current += 1;
      setIsDraggingFiles(true);
    },
    [disabled],
  );

  const onDragOver = useCallback(
    (event: DragEvent) => {
      if (disabled || !dragCarriesFiles(event)) {
        return;
      }
      // Mandatory: without preventDefault on dragover the drop never fires.
      event.preventDefault();
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = DropEffect.Copy;
      }
    },
    [disabled],
  );

  const onDragLeave = useCallback(
    (event: DragEvent) => {
      // Decrement based on tracked depth rather than re-checking the event:
      // every matching enter incremented, so any leave during an active file
      // drag pairs with it — even if this leave no longer reports `types`.
      if (disabled || dragDepthRef.current === 0) {
        return;
      }
      event.preventDefault();
      dragDepthRef.current -= 1;
      if (dragDepthRef.current === 0) {
        setIsDraggingFiles(false);
      }
    },
    [disabled],
  );

  const onDrop = useCallback(
    (event: DragEvent) => {
      // Always clear the drag state on any drop so the overlay can never stick.
      resetDrag();
      if (disabled || !dragCarriesFiles(event)) {
        return;
      }
      // Prevent the browser's default "navigate to / open the file" for EVERY
      // file drop — even one we extract nothing from (a directory, or a type
      // the entry API can't materialize). This is what stops a new tab opening.
      event.preventDefault();
      const droppedFiles = readDroppedFiles(event.dataTransfer);
      if (droppedFiles.length > 0) {
        onFilesDropped(droppedFiles);
      }
    },
    [disabled, onFilesDropped, resetDrag],
  );

  // Safety net: a drag can end without a final `dragleave` on the zone (the
  // pointer leaves the window, the drop lands elsewhere, or the drag is
  // cancelled). Catch those at the window level while a drag is active.
  useEffect(() => {
    if (!isDraggingFiles) {
      return;
    }
    const handleDragEnd = (): void => resetDrag();
    window.addEventListener("drop", handleDragEnd);
    window.addEventListener("dragend", handleDragEnd);
    return () => {
      window.removeEventListener("drop", handleDragEnd);
      window.removeEventListener("dragend", handleDragEnd);
    };
  }, [isDraggingFiles, resetDrag]);

  // A file dropped anywhere on the page must never open a new tab. The zone's
  // own handler covers drops it captures; this guards drops the zone's drag
  // detection missed or that landed outside the zone — prevent the browser
  // default for every file drag at the window level.
  useEffect(() => {
    const preventFileDropNavigation = (event: globalThis.DragEvent): void => {
      if (dataTransferHasFiles(event.dataTransfer)) {
        event.preventDefault();
      }
    };
    window.addEventListener("dragover", preventFileDropNavigation);
    window.addEventListener("drop", preventFileDropNavigation);
    return () => {
      window.removeEventListener("dragover", preventFileDropNavigation);
      window.removeEventListener("drop", preventFileDropNavigation);
    };
  }, []);

  // Never surface an active drag while disabled — this both hides the overlay
  // and ensures becoming disabled mid-drag can't leave it stuck on, without an
  // in-effect setState.
  return {
    isDraggingFiles: isDraggingFiles && !disabled,
    dropZoneHandlers: { onDragEnter, onDragOver, onDragLeave, onDrop },
  };
}
